<template>
  <slot
    name="any"
    :data="result?.data"
    :refresh="launchGettingFlow"
    :loading="loading"
    :result="result"
    :loadingError="loadingError"
  >
    <slot
      v-if="loadingError"
      :refresh="launchGettingFlow"
      :result="result"
      name="error"
    >
      <NoDataView :refresh="launchGettingFlow" :result="result"></NoDataView>
    </slot>

    <slot
      v-else
      :data="result?.data"
      :refresh="launchGettingFlow"
      :loading="loading"
    >
      <div v-html="Commons.jsonToHtml(result?.data)"></div>
    </slot>
  </slot>
  <EventListener
    :events="[
      EVENTS.APPLICATION_WORK_FINISHED,
      ...(typeof refreshOnEvents == 'string'
        ? [refreshOnEvents]
        : refreshOnEvents),
    ]"
    @eventReceived="silentRefresh"
  />
</template>

<script>
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'
import Service from '../../dataService.js'
import NoDataView from './NoDataView.vue'
import EventListener from '@/events/EventListener.vue'
import { EVENTS } from '@/shared/constants/commons.js'
import AppError from '@/shared/errors/appError.js'
import sendBugAlert from '@/shared/errors/sendBugAlert.js'
import Commons from '@/shared/utils/commons.js'

export default defineComponent({
  name: 'DataFetcher',
  emits: ['fetched', 'beforeGetting', 'startGetting', 'loadingError'],
  components: { NoDataView, EventListener },
  props: {
    dataKey: { type: String, required: true },
    filters: { type: Object, default: () => ({}) },
    refreshDelay: { type: Number, default: 15000 },
    refreshOnEvents: { type: [String, Array], default: '' },
    debounceDelay: { type: Number, default: 1000 },
    log: { type: Boolean, default: false },
  },
  setup() {
    return { Commons, debounceGetting: ({ reqId }) => {}, EVENTS }
  },
  data() {
    return {
      result: {
        statusCode: 200,
        message: '',
        data: null,
        error: null,
        timestamp: null,
      },
      loading: true,
      loadingError: false,
      isRefreshing: false,

      refreshInterval: 0,
      eventsIds: [],

      lastRequestId: '',
      lastResolvedRequest: '',
    }
  },
  mounted() {
    this.fetchData()
    this.initAutoRefresh()
    this.initDebounceGetting()
  },
  beforeUnmount() {
    if (this.refreshInterval) clearInterval(this.refreshInterval)
    this.eventsIds.forEach(Service.events.removeHandler)
  },
  methods: {
    initAutoRefresh() {
      if (this.refreshDelay) {
        this.refreshInterval = setInterval(() => {
          if (!this.loadingError && !this.loading && !this.isRefreshing) {
            this.silentRefresh()
          }
        }, this.refreshDelay)
      }
    },
    silentRefresh() {
      this.isRefreshing = true
      this.fetchData({ silent: true }).finally(() => {
        this.isRefreshing = false
      })
    },

    async fetchData({ silent = false, reqId = null } = {}) {
      if (!silent) {
        this.loading = true
        this.loadingError = false
        this.$emit('startGetting')
      }

      const requestId = reqId || `${Date.now()}`
      this.lastRequestId = requestId

      if (this.log) Commons.log('🚀 fetchData ~ key:', this.dataKey)

      let perfStart = Date.now()
      let result
      const key = this.dataKey
      const params = { ...this.filters }
      try {
        result = await Service.getSingleData(this.dataKey, params)
        if (this.lastRequestId !== requestId) return
        if (!result) throw new Error('Error while getting data')
        const hasError = result.statusCode !== 200 || !result.data
        if (hasError && silent) return
        this.result = {
          statusCode: result.statusCode,
          message: result.message,
          data: result.data,
          error: result.error,
          timestamp: Date.now(),
        }
        this.lastResolvedRequest = requestId
        if (hasError) throw new Error('Bad response from server')
        this.emitFetched()
      } catch (error) {
        if (silent) return
        Commons.log('🚀 ~ fetchData ~ error:', error, result)
        if (!result) {
          this.result = {
            statusCode: new AppError().statusCode,
            message: error.message,
            data: null,
            error: new AppError(error.message),
            timestamp: Date.now(),
          }
        }
        sendBugAlert(result.error, { key, params, result }, false)
        this.loadingError = true
        this.emitFetched()
        this.$emit('loadingError')
      }

      this.loading = false

      if (this.log) {
        Commons.log(
          '🚀 fetchData ~ perf:',
          this.dataKey,
          `${Date.now() - perfStart}ms`,
        )
      }
    },

    emitFetched() {
      this.$emit('fetched', {
        data: this.result.data,
        result: this.result,
        error: null,
      })
    },

    isLoading() {
      return this.lastResolvedRequest !== this.lastRequestId
    },

    initDebounceGetting() {
      this.debounceGetting = debounce(({ reqId } = {}) => {
        this.fetchData({ reqId })
      }, this.debounceDelay)
    },
    launchGettingFlow() {
      if (this.log) Commons.log('🚀 launchGettingFlow ~', this.dataKey)
      this.$emit('beforeGetting')
      this.loading = true
      this.loadingError = false
      this.debounceGetting({ reqId: `${Date.now()}` })
    },
    refresh() {
      this.launchGettingFlow()
    },
  },
  watch: {
    refreshOnEvents(newVal) {
      if (this.log) Commons.log('props updated refreshOnEvents', newVal)
      this.launchGettingFlow()
    },
    contentId(newVal) {
      if (this.log) Commons.log('props updated contentId', newVal)
      this.launchGettingFlow()
    },
    filters: {
      deep: true,
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          if (this.log) Commons.log('props updated filters', newVal)
          this.launchGettingFlow()
        }
      },
    },
  },
})
</script>
